import { useRef } from 'react';
import type { EquipmentDefault, LocationUnavailable } from '@heraion/shared';

import type { PlanStarterHydrationOptions } from './planStarterHydration';
import { getLastKnownPlace, getSilentInstanceAt } from './silentInstance';
import { loadPlateLoadShape, useRoundingContextRef, type PlateLoadShape } from './state/plateConfig';

// What a plan starter is hydrated with, in one place: the session start
// (useDraftPersistence) and the row «More time» brings back (#356). The
// device's finest loadable step (LG-15), the load shape the athlete saved per
// exercise, and the silent machine of the place (EQ-1), as the add path.
export function useStarterHydrationOptions({
  locationUnavailable,
  equipmentDefault,
}: {
  locationUnavailable?: LocationUnavailable;
  equipmentDefault?: EquipmentDefault;
}): (slugs: readonly string[]) => Promise<PlanStarterHydrationOptions> {
  const roundingContextRef = useRoundingContextRef();
  // The profile rides a ref: a late-loading profile is read at call time.
  const profileRef = useRef({ locationUnavailable, equipmentDefault });
  profileRef.current = { locationUnavailable, equipmentDefault };
  const resolveInstance = (slug: string): string | null =>
    getSilentInstanceAt(
      slug,
      getLastKnownPlace(),
      profileRef.current.locationUnavailable,
      profileRef.current.equipmentDefault,
    );
  return async (slugs) => {
    // The floor of a derived weight follows the saved shape before the catalog.
    const shapes = await Promise.all(slugs.map(async (s) => [s, await loadPlateLoadShape(s)] as const));
    const loadShapes = new Map<string, PlateLoadShape>();
    for (const [slug, shape] of shapes) if (shape) loadShapes.set(slug, shape);
    return { loadableContext: roundingContextRef.current, resolveInstance, loadShapes };
  };
}
